/**
 * PATH: packages/ui/src/components/email/row/RowMeta.tsx
 * FILE_ID: UI-EMAIL-ROW-META-001
 * PURPOSE: The row's classification + attribution chip run, split into two
 *          zones on ONE line: a PINNED lead (never scrolls, never clips) for
 *          the highest-signal chips — category · Board>Lane work / "Add to
 *          board" — and a SCROLLING tail for everything lower-signal, led by
 *          the mailbox-attribution pill so "which mailbox" is answered at rest.
 *          SINGLE-LINE at every breakpoint so all rows stay the same height.
 * WHY:     The pre-refactor row let ~12 chips share a scrolling strip that
 *          duplicated across a framed top-strip + an unframed footer. One
 *          fixed, ordered run (highest-signal first) was the cure for "the tags
 *          are all over … ux is kinda broken" (FR-0638). But a SINGLE scrolling
 *          strip then clipped the category ("cutting off System") because the
 *          visibility badge led and pushed it, and stranded the mailbox pill at
 *          the trailing edge where it scrolled off first — so the operator
 *          (2026-06-21, screenshot) couldn't see the category OR tell "which
 *          mailbox it is from". Splitting into a pinned lead + scrolling tail
 *          keeps the two things you must always see (what kind of email · what
 *          board it's on / can go on) fixed, and puts mailbox first in the tail
 *          so it shows at rest on a normal-width column. The attribution chips
 *          still live here (relocated off the identity line per
 *          BUG-2026-06-20-EMAIL-ROW-NAME-CRUSH), so the sender name keeps the
 *          whole top row and this line adds ZERO height (the action cluster
 *          already shares it).
 * FLOW:    [ category · work / allocate ]·pinned   ‖   [ mailbox · visibility ·
 *          suggested · attach · calendar · tags · relation · company ]·scrolls.
 * RELATED:
 *   - row/rowGrammar.ts: CHIP_STRIP_LAYOUT (the no-wrap contract for the tail).
 *   - row/RowWorkChip.tsx: the work chip rendered into the `workChip` slot.
 */
import type { ReactNode } from 'react'
import { Calendar, Paperclip } from 'lucide-react'

import { cn } from '../../../lib/utils'
import type { EmailMessage } from '../types'
import { CHIP_BASE, CHIP_STRIP_LAYOUT } from './rowGrammar'

export interface RowMetaProps {
  email: Pick<EmailMessage, 'has_attachments' | 'has_calendar_event' | 'tags'>
  /** The "one category pill" — host's <CategoryChip>, or null to suppress.
   *  Sits in the PINNED lead so it can never clip ("cutting off System"). */
  categoryChip?: ReactNode
  /** The Board>Lane (or linked-job) work chip (RowWorkChip output). Pinned. */
  workChip?: ReactNode
  /** "Add to board" affordance for an unallocated row — the work chip's
   *  unallocated counterpart, pinned in the same lead slot. Mutually exclusive
   *  with `workChip` (the composer only passes one). */
  allocateChip?: ReactNode
  /** AI "Suggested job" chip on unallocated rows. */
  suggestedJobChip?: ReactNode
  /** Visibility (private/on-board) indicator — folded INTO this chip line as a
   *  compact glyph rather than a full pill on its own line, so a pending row
   *  doesn't grow an extra row of height (operator: "cluttered the
   *  classification tiling"). Leads the line so the lock reads first. */
  visibilityBadge?: ReactNode
  /** Attribution chips relocated OFF the identity line so the sender name owns
   *  the top row. `mailboxChip` leads the scrolling tail (so "which mailbox" is
   *  answered at rest on a normal-width column); `relationChip` / `companyChip`
   *  are the lowest-signal glances and sit last (first to scroll off). */
  relationChip?: ReactNode
  companyChip?: ReactNode
  mailboxChip?: ReactNode
  /** Counterparty ROLE chip (W5-V12, "Supplier"/"Client"/...). Sits with the
   *  other sender-identity glances, just before the company chip. */
  roleChip?: ReactNode
}

/**
 * RowMeta(email, slots) → the row's single-line classification chip run.
 * Renders null when there is genuinely nothing to show (keeps quiet rows
 * from reserving an empty line).
 */
export function RowMeta({
  email,
  categoryChip,
  workChip,
  allocateChip,
  suggestedJobChip,
  visibilityBadge,
  relationChip,
  companyChip,
  mailboxChip,
  roleChip,
}: RowMetaProps) {
  const hasTags = !!email.tags && email.tags.length > 0
  // PINNED lead — the two things the operator must ALWAYS see: what kind of
  // email (category) and what board it's on / can go on (work or allocate).
  // shrink-0 so they never clip ("cutting off System") under the action
  // cluster; each chip carries its own max-w cap so the lead stays bounded.
  const hasLead = !!categoryChip || !!workChip || !!allocateChip
  // SCROLLING tail — mailbox first (so "which mailbox" shows at rest), then the
  // low-signal glances that may scroll off on a narrow column.
  const hasTail =
    !!mailboxChip ||
    !!visibilityBadge ||
    !!suggestedJobChip ||
    email.has_attachments ||
    email.has_calendar_event ||
    hasTags ||
    !!relationChip ||
    !!companyChip ||
    !!roleChip
  if (!hasLead && !hasTail) return null

  return (
    <div className="min-w-0 flex items-center gap-1.5">
      {hasLead && (
        // min-w-0 (NOT shrink-0): the lead must be allowed to SHRINK so its
        // self-truncating chips (category pill + Board>Lane tag, each capped by
        // its own max-w) give way under width pressure instead of overflowing
        // their lane and painting over the trailing content
        // (BUG-2026-06-21-EMAIL-ROW-CHIP-SPILL). NO overflow-hidden here — that
        // would clip the category / lane popovers that anchor off these chips.
        <div className="flex items-center gap-1.5 min-w-0">
          {categoryChip}
          {workChip}
          {allocateChip}
        </div>
      )}
      {hasTail && (
        <div
          className={cn('min-w-0 flex-1', CHIP_STRIP_LAYOUT)}
          // Right-edge fade so an overflowing strip is never SILENTLY clipped
          // (operator 2026-06-21: "things missing in the bottom expansion").
          // The scrollbar is hidden by CHIP_STRIP_LAYOUT, so this gradient is
          // the only "there's more — scroll" affordance. It only bites when a
          // chip actually reaches the last 14px, so a strip that fits shows no
          // visible fade (the faded zone is empty background).
          style={{
            maskImage: 'linear-gradient(to right, #000 calc(100% - 14px), transparent)',
            WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 14px), transparent)',
          }}
        >
          {/* Mailbox attribution leads the tail — highest-signal of the
              demoted chips, so it shows at rest before anything scrolls. */}
          {mailboxChip}
          {visibilityBadge}
          {suggestedJobChip}
          {email.has_attachments && (
            <Paperclip className="h-3 w-3 text-muted-foreground/50 shrink-0" />
          )}
          {email.has_calendar_event && (
            <span
              title="Calendar invite"
              className={cn(
                CHIP_BASE,
                'shrink-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
              )}
            >
              <Calendar className="h-2.5 w-2.5" />
              Invite
            </span>
          )}
          {email.tags?.map((tag) => (
            // CHIP_BASE keeps tags on the uniform 10px floor (was text-[8px] — a
            // core "hard to read" complaint, operator 2026-06-21) and the same
            // shape/height as every other chip in the strip.
            <span
              key={tag.id}
              className={cn(CHIP_BASE, 'shrink-0 text-white')}
              style={{ backgroundColor: tag.color }}
            >
              {tag.name}
            </span>
          ))}
          {/* Lowest-signal glances — first to scroll off on a narrow column.
              roleChip precedes companyChip: "who they are to us" reads before
              "which company" (both are sender-identity answers, W5-V12). */}
          {relationChip}
          {roleChip}
          {companyChip}
        </div>
      )}
    </div>
  )
}
